import React from 'react'
import { translate } from '../utils/useful'
import { siteTheme } from '../variables/config'

/**
 * Small, single-row pagination for embedded tables (e.g. TableWidget).
 * currentPage is 0-based page index, matching Pagination / getRecords body.skip usage.
 */
function CompactPagination({ currentPage, totalCount, limit, changePage }) {
    const total = totalCount ?? 0
    const pages = Math.ceil(total / (limit || 1))
    if (!Number.isFinite(pages) || pages <= 1) {
        return null
    }

    const page = Math.min(Math.max(0, currentPage), pages - 1)
    const go = (i) => {
        const next = Math.max(0, Math.min(pages - 1, i))
        changePage(next)
    }

    const idxSet = new Set([0, pages - 1, page - 1, page, page + 1].filter((i) => i >= 0 && i < pages))
    const sorted = [...idxSet].sort((a, b) => a - b)

    /** @type {Array<number|'…'>} */
    const segments = []
    sorted.forEach((n, idx) => {
        if (idx > 0 && n - sorted[idx - 1] > 1) {
            segments.push('…')
        }
        segments.push(n)
    })

    const accent = siteTheme?.primaryColors?.main1 ?? 'rgb(10, 45, 91)'
    const muted = '#94a3b8'
    const line = '#e2e8f0'

    const btnBase = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 26,
        height: 26,
        padding: '0 6px',
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1,
        border: `1px solid ${line}`,
        borderRadius: 6,
        background: '#fff',
        color: '#475569',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'background 0.15s ease, border-color 0.15s ease, color 0.15s ease',
    }

    const activeStyle = {
        ...btnBase,
        background: accent,
        borderColor: accent,
        color: '#fff',
        cursor: 'default',
        boxShadow: `0 1px 2px rgba(10, 45, 91, 0.2)`,
    }

    const navBtn = (disabled) => ({
        ...btnBase,
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        padding: '0 8px',
    })

    return (
        <div
            className="d-flex align-items-center justify-content-center flex-nowrap w-100"
            style={{
                gap: 3,
                fontSize: 11,
                paddingTop: 4,
                paddingBottom: 2,
                userSelect: 'none',
                direction: 'ltr',
            }}
        >
            <button
                type="button"
                disabled={page <= 0}
                onClick={() => go(page - 1)}
                style={navBtn(page <= 0)}
                aria-label={translate('Prev')}
            >
                ‹
            </button>

            <div className="d-flex align-items-center flex-nowrap" style={{ gap: 3 }}>
                {segments.map((seg, si) =>
                    seg === '…' ? (
                        <span key={`e-${si}`} style={{ padding: '0 2px', color: muted, fontSize: 12 }}>
                            …
                        </span>
                    ) : (
                        <button
                            key={seg}
                            type="button"
                            onClick={() => go(seg)}
                            style={seg === page ? activeStyle : btnBase}
                            aria-current={seg === page ? 'page' : undefined}
                            aria-label={`${translate('Page')} ${seg + 1}`}
                        >
                            {seg + 1}
                        </button>
                    )
                )}
            </div>

            <button
                type="button"
                disabled={page >= pages - 1}
                onClick={() => go(page + 1)}
                style={navBtn(page >= pages - 1)}
                aria-label={translate('Next')}
            >
                ›
            </button>

            <span
                style={{
                    color: muted,
                    fontSize: 10,
                    fontWeight: 500,
                    marginInlineStart: 4,
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}
            >
                {page + 1}/{pages}
            </span>
        </div>
    )
}

export default CompactPagination
